import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { FloatingAdinkra } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';

const briefings = [
  {
    id: 'tyt-kokus',
    title: 'TyT-KoKus',
    tagline: 'Scaling African Fashion E-Commerce',
    status: 'Concept Brief',
    vision:
      'A digital-first fashion house making authentic African garments — kente, ankara, adire, kaftans — accessible to a global diaspora and conscious consumers, while paying artisans fairly and preserving heritage craft.',
    pillars: [
      {
        label: 'Curated Marketplace',
        detail:
          'A tightly curated catalog of African designers and ateliers. Not mass-market — each drop is limited, tagged with the maker and the region the cloth comes from.',
      },
      {
        label: 'Artisan Equity Model',
        detail:
          'Weavers and tailors keep a transparent share of every sale. A portion of revenue funds apprenticeship stipends so the craft passes to the next generation.',
      },
      {
        label: 'Diaspora Logistics',
        detail:
          'Consolidated cross-border shipping from Accra to the UK, US, and EU. Pre-paid duties and a no-surprise checkout experience — the #1 friction point the diaspora currently faces.',
      },
      {
        label: 'Story-Led Commerce',
        detail:
          'Every product page is a short story: the cloth, the maker, the symbolism. Fashion as identity, not just apparel.',
      },
    ],
    targetMarket:
      'African diaspora (30–55), Afro-conscious consumers globally, event buyers (weddings, outdooring, funerals).',
    whyNow:
      'Global appetite for sustainable, origin-traceable fashion is rising. African design is having its moment — but the supply chain and buying experience are broken. TyT-KoKus fixes the pipe.',
  },
  {
    id: 'crushed-diamond',
    title: 'Crushed Diamond',
    tagline: 'A Royal Lifestyle Brand Rooted in African Identity',
    status: 'Brand System Live',
    vision:
      'A luxury lifestyle brand built around the Crushed Diamond philosophy: pressure transforms, heritage refines, and true value emerges from the grinding. Spanning fragrance, fashion, home, and editorial content under a unified royal dark aesthetic.',
    pillars: [
      {
        label: 'Brand Identity System',
        detail:
          'Palette: obsidian black, royal gold (#C9A84C), warm ivory, tan. Typography: Cormorant Garamond display + DM Sans body. Adinkra and kente motifs as signature texture language.',
      },
      {
        label: 'Fragrance Line (Flagship)',
        detail:
          'A signature scent collection inspired by West African botanicals — shea blossom, frankincense, vetiver, cocoa absolute. Positioned against Western niche houses, priced for aspirational luxury.',
      },
      {
        label: 'Editorial & Community',
        detail:
          'The brand publishes — essays, lookbooks, founder interviews — to build cultural gravity around the products. Crushed Diamond is a point of view before it is a price tag.',
      },
      {
        label: 'Ecosystem Crossovers',
        detail:
          'Capsule collaborations with TyT-KoKus (fashion), Avenue Lincoln (hospitality scenting), and Havilah (private gifting). The brand is the connective tissue.',
      },
    ],
    targetMarket:
      'Affluent African professionals, diaspora tastemakers, gifting market, boutique hospitality.',
    whyNow:
      'The brand system (guide, palette, voice) is already codified. The portfolio site you are looking at runs on its palette. First commercial capsule is next.',
  },
  {
    id: 'havilah',
    title: 'Project Havilah',
    tagline: 'A Unified Ecosystem — Trade, Gold, Community',
    status: 'Strategic Vision',
    vision:
      'Havilah is an ecosystem play — not a single company. The ambition: link ethically-sourced gold trading, community profit-sharing, and enterprise services under one governance model, with Ghana as the anchor market and the African diaspora as the capital base.',
    pillars: [
      {
        label: 'Havilah Gold Trading',
        detail:
          'Small-scale, traceable gold sourcing from licensed Ghanaian cooperatives. Direct export relationships bypassing predatory middlemen. Compliance-first, audit-ready.',
      },
      {
        label: 'Havilah Hub Ghana',
        detail:
          'A profit-sharing enterprise platform — members contribute capital and receive a defined share of ecosystem earnings. Structured as a term-sheeted participation model, not a vague "community".',
      },
      {
        label: 'Unified Ecosystem Vision',
        detail:
          'Gold, hospitality, SME services, and property management feed each other. A member who joins through gold trading can access Avenue Lincoln stays, SME Hub training, and Crushed Diamond gifting — one identity across the stack.',
      },
      {
        label: 'Diaspora Capital',
        detail:
          'Designed for Ghanaians abroad who want real, instrumented exposure to the home economy — not remittances, not charity, not vibes. Governance, reporting, exits.',
      },
    ],
    targetMarket:
      'Ghanaian diaspora investors, ethical commodity buyers, enterprise members seeking structured African exposure.',
    whyNow:
      'Ghana\'s small-scale gold sector is being formalized. Diaspora investment vehicles are underbuilt. The window to build the trusted rails is now — before extractive foreign platforms claim it.',
  },
];

const FinePrintPage = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="adinkra-texture-bg" style={{ padding: '5rem 2rem', position: 'relative' }}>
      <FloatingAdinkra count={4} color="rgba(107, 85, 32, 0.05)" />
      <div style={{ maxWidth: '920px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <RevealOnScroll>
          <div className="section-header">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold)', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              <Lock size={12} /> The Fine Print
            </div>
            <h2 className="section-title" style={{ fontSize: '2.2rem' }}>Concepts in the Vault</h2>
            <p className="section-subtitle" style={{ fontSize: '1rem' }}>
              Early-stage ventures and brand systems — briefings for the curious.
            </p>
          </div>
          <div className="kente-border"></div>
        </RevealOnScroll>

        <div style={{ display: 'grid', gap: '1rem', marginTop: '2rem' }}>
          {briefings.map((item, idx) => {
            const isOpen = expanded === item.id;
            return (
              <RevealOnScroll key={item.id} delay={idx * 120} direction="up">
                <div className={`fineprint-card ${isOpen ? 'open' : ''}`}>
                  <button
                    className="fineprint-toggle"
                    onClick={() => setExpanded(isOpen ? null : item.id)}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <h4 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--gold)' }}>{item.title}</h4>
                        <span style={{ fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '0.2rem 0.55rem', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '2px', color: 'var(--gold-satin)' }}>
                          {item.status}
                        </span>
                      </div>
                      <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{item.tagline}</p>
                    </div>
                    {isOpen ? <ChevronUp size={20} color="var(--gold)" /> : <ChevronDown size={20} color="var(--gold)" />}
                  </button>

                  {isOpen && (
                    <div className="fineprint-content">
                      <div style={{ marginBottom: '1.25rem' }}>
                        <div className="fineprint-label">Vision</div>
                        <p>{item.vision}</p>
                      </div>

                      <div style={{ marginBottom: '1.25rem' }}>
                        <div className="fineprint-label">The Pillars</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem', marginTop: '0.5rem' }}>
                          {item.pillars.map((p) => (
                            <div key={p.label} className="fineprint-pillar">
                              <h5>{p.label}</h5>
                              <p>{p.detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                        <div>
                          <div className="fineprint-label">Target Market</div>
                          <p>{item.targetMarket}</p>
                        </div>
                        <div>
                          <div className="fineprint-label">Why Now</div>
                          <p>{item.whyNow}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </RevealOnScroll>
            );
          })}
        </div>

        <RevealOnScroll delay={400} direction="scale">
          <p style={{ textAlign: 'center', marginTop: '3rem', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', color: 'var(--text-dark-secondary)', fontSize: '0.95rem' }}>
            "Every ecosystem begins as a sketch. These are mine — not hidden, just waiting their turn."
          </p>
        </RevealOnScroll>
      </div>
    </div>
  );
};

export default FinePrintPage;
